"use client";

import {
    createContext,
    useContext,
    useRef,
    type ReactNode,
} from "react";
import { useStore } from "zustand";
import { useStoreWithEqualityFn } from "zustand/traditional";
import { shallow } from "zustand/shallow";
import { createEditorStore, type EditorStore } from "./createEditorStore";
import type { EditorState, EditorStoreInit } from "./types";

const EditorStoreContext = createContext<EditorStore | null>(null);

export function EditorStoreProvider({
    init,
    children,
}: {
    init: EditorStoreInit;
    children: ReactNode;
}) {
    const storeRef = useRef<EditorStore>();
    if (!storeRef.current) storeRef.current = createEditorStore(init);
    return (
        <EditorStoreContext.Provider value={storeRef.current}>
            {children}
        </EditorStoreContext.Provider>
    );
}

export function useEditorStoreApi(): EditorStore {
    const store = useContext(EditorStoreContext);
    if (!store) {
        throw new Error(
            "useEditorStore must be used within an EditorStoreProvider"
        );
    }
    return store;
}

/** Subscribe to a slice of editor state (Object.is equality). */
export function useEditorStore<T>(selector: (state: EditorState) => T): T {
    return useStore(useEditorStoreApi(), selector);
}

/** Subscribe with shallow equality — for selecting objects (e.g. action groups). */
export function useEditorStoreShallow<T>(selector: (state: EditorState) => T): T {
    return useStoreWithEqualityFn(useEditorStoreApi(), selector, shallow);
}

/** Read the zundo temporal (undo/redo history) store reactively. */
export function useTemporalStore<T>(
    selector: (state: TemporalState) => T
): T {
    const store = useEditorStoreApi();
    return useStore(store.temporal, selector);
}

export interface TemporalState {
    pastStates: unknown[];
    futureStates: unknown[];
    undo: (steps?: number) => void;
    redo: (steps?: number) => void;
    clear: () => void;
    pause: () => void;
    resume: () => void;
    isTracking: boolean;
}
